// ─────────────────────────────────────────────────────────────────────
// /admin/user-permissions — IT-Admin tool for granting individual
// permissions to specific employees. Built in Round 19 (2026).
//
// Two-pane layout:
//   LEFT  ▸ searchable employee list
//   RIGHT ▸ snapshot for the selected employee:
//             - identity row (name / dept / role)
//             - table of currently effective permissions, each with a
//               source badge (Role / Department / Direct)
//             - "+ Grant permission" picker that lists permissions the
//               user doesn't currently have
//
// Only DIRECT grants are revocable here. Role and Department grants
// must be removed by changing the user's role / department in other
// admin tools — surfaced with a disabled revoke button.
// ─────────────────────────────────────────────────────────────────────
import React, { useEffect, useMemo, useState } from "react";
import {
    Container, Card, CardHeader, CardBody, Row, Col,
    Input, Button, Spinner, Badge, Alert,
    Table, Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import { DashboardPageHeader } from "../../../Components/Common/DashboardKit";
import {
    fetchPermissionCatalog,
    fetchEmployees,
    fetchUserPermissions,
    grantPermission,
    revokePermission,
} from "../../../api/userPermissions";

const SOURCE_COLORS = {
    Direct:     "primary",
    Role:       "secondary",
    Department: "info",
    Other:      "secondary",
};

export default function UserPermissionsPage() {
    document.title = "User Permissions | SSW Technologies";

    // ── catalog + employee list (loaded once) ─────────────────────────
    const [catalog,   setCatalog]   = useState([]);     // [{ permissionName, description }]
    const [employees, setEmployees] = useState([]);     // [{ empId, name, department, role, roleId }]
    const [bootLoading, setBootLoading] = useState(true);
    const [bootError,   setBootError]   = useState(null);

    // ── selection + snapshot ──────────────────────────────────────────
    const [search,        setSearch]        = useState("");
    const [selectedEmpId, setSelectedEmpId] = useState(null);
    const [snapshot,      setSnapshot]      = useState(null);
    const [snapshotLoading, setSnapshotLoading] = useState(false);
    const [snapshotError,   setSnapshotError]   = useState(null);

    // ── action state ──────────────────────────────────────────────────
    const [working, setWorking] = useState(false);
    const [grantOpen, setGrantOpen] = useState(false);
    const [pendingPerm, setPendingPerm] = useState("");

    // Bootstrap loaders
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setBootLoading(true); setBootError(null);
            try {
                const [perms, emps] = await Promise.all([
                    fetchPermissionCatalog(),
                    fetchEmployees(),
                ]);
                if (cancelled) return;
                setCatalog(perms);
                setEmployees(emps);
            } catch (e) {
                if (!cancelled) setBootError("Could not load permission catalog or employee list.");
            } finally {
                if (!cancelled) setBootLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Re-fetch snapshot whenever the selected user changes.
    useEffect(() => {
        if (!selectedEmpId) { setSnapshot(null); return; }
        let cancelled = false;
        (async () => {
            setSnapshotLoading(true); setSnapshotError(null);
            try {
                const snap = await fetchUserPermissions(selectedEmpId);
                if (cancelled) return;
                if (!snap) setSnapshotError("Employee not found.");
                else setSnapshot(snap);
            } catch (e) {
                if (!cancelled) setSnapshotError("Could not load this user's permissions.");
            } finally {
                if (!cancelled) setSnapshotLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [selectedEmpId]);

    // Filter employee list by the search box. Matches against empId, name,
    // department, and role — case-insensitive.
    const filteredEmployees = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return employees;
        return employees.filter((e) =>
            (e.empId      || "").toLowerCase().includes(q) ||
            (e.name       || "").toLowerCase().includes(q) ||
            (e.department || "").toLowerCase().includes(q) ||
            (e.role       || "").toLowerCase().includes(q)
        );
    }, [employees, search]);

    // Permissions in the catalog the selected user doesn't already have.
    // Used to populate the "+ Grant" picker.
    const availableToGrant = useMemo(() => {
        if (!snapshot) return [];
        const have = new Set(
            (snapshot.permissions || []).map((p) => p.permissionName.toLowerCase())
        );
        return catalog.filter((p) => !have.has(p.permissionName.toLowerCase()));
    }, [snapshot, catalog]);

    const openGrant = () => {
        setPendingPerm(availableToGrant[0]?.permissionName || "");
        setGrantOpen(true);
    };

    const onGrant = async () => {
        if (!pendingPerm) return;
        setWorking(true);
        try {
            const fresh = await grantPermission(selectedEmpId, pendingPerm);
            setSnapshot(fresh);
            setGrantOpen(false);
        } catch (e) {
            window.alert(e?.response?.data?.error || "Could not grant — please retry.");
        } finally {
            setWorking(false);
        }
    };

    const onRevoke = async (permissionName) => {
        if (!window.confirm(`Remove "${permissionName}" from ${snapshot.employee.name}?`)) return;
        setWorking(true);
        try {
            const fresh = await revokePermission(selectedEmpId, permissionName);
            setSnapshot(fresh);
        } catch (e) {
            window.alert(e?.response?.data?.error || "Could not revoke — please retry.");
        } finally {
            setWorking(false);
        }
    };

    // ── render ────────────────────────────────────────────────────────
    return (
        <div className="page-content">
            <Container fluid>
                {/* Page header */}
                <DashboardPageHeader
                    icon="ri-shield-keyhole-line"
                    eyebrow="Administration"
                    title="User Permissions"
                    subtitle="Grant individual permissions to specific employees, on top of their role."
                    tone="rose"
                    breadcrumb={[
                        { label: "Dashboards", to: "/" },
                        { label: "Admin", to: "/admin" },
                        { label: "User Permissions" },
                    ]}
                />

                <Alert color="info" className="d-flex align-items-center mb-3">
                    <i className="ri-information-line me-2 fs-18" />
                    <span>
                        Permission changes take effect the next time the user signs in
                        (or refreshes their session) — their access is cached at login.
                        Ask them to log out and back in if a new grant doesn't appear right away.
                    </span>
                </Alert>

                {bootLoading ? (
                    <div className="text-center text-muted py-5"><Spinner size="sm" /> Loading…</div>
                ) : bootError ? (
                    <div className="alert alert-danger">{bootError}</div>
                ) : (
                    <Row className="g-3">
                        {/* ── LEFT: employee picker ─────────────────────────── */}
                        <Col lg={4}>
                            <Card className="border-0 shadow-sm h-100">
                                <CardHeader className="bg-white border-0 pt-3 px-3 pb-2">
                                    <h6 className="fw-semibold mb-2">Employees</h6>
                                    <Input
                                        type="search"
                                        placeholder="Search by name, empId, dept, role…"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        bsSize="sm"
                                    />
                                </CardHeader>
                                <CardBody className="p-2" style={{ maxHeight: "calc(100vh - 320px)", overflowY: "auto" }}>
                                    {filteredEmployees.length === 0 ? (
                                        <div className="text-muted small text-center py-4">No matches.</div>
                                    ) : (
                                        <ul className="list-unstyled mb-0">
                                            {filteredEmployees.map((e) => (
                                                <li key={e.empId}>
                                                    <button
                                                        type="button"
                                                        onClick={() => setSelectedEmpId(e.empId)}
                                                        className="w-100 text-start border-0 rounded p-2 mb-1"
                                                        style={{
                                                            background: e.empId === selectedEmpId ? "rgba(91, 80, 214, 0.10)" : "transparent",
                                                            color:      e.empId === selectedEmpId ? "var(--ssw-ink-indigo, #2c2076)" : "var(--ssw-fg-1, #212529)",
                                                            cursor: "pointer",
                                                        }}
                                                    >
                                                        <div className="fw-medium" style={{ fontSize: 13 }}>{e.name || e.empId}</div>
                                                        <div className="text-muted" style={{ fontSize: 11 }}>
                                                            {e.empId}
                                                            {e.role       ? ` · ${e.role}`       : ""}
                                                            {e.department ? ` · ${e.department}` : ""}
                                                        </div>
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>

                        {/* ── RIGHT: selected user's permissions ─────────────── */}
                        <Col lg={8}>
                            <Card className="border-0 shadow-sm h-100">
                                {!selectedEmpId ? (
                                    <CardBody className="text-center text-muted py-5">
                                        <i className="ri-arrow-left-line" style={{ fontSize: 32, opacity: 0.4 }}></i>
                                        <div className="mt-2">Pick an employee on the left.</div>
                                    </CardBody>
                                ) : snapshotLoading ? (
                                    <CardBody className="text-center text-muted py-5">
                                        <Spinner size="sm" /> Loading…
                                    </CardBody>
                                ) : snapshotError ? (
                                    <CardBody>
                                        <div className="alert alert-danger mb-0">{snapshotError}</div>
                                    </CardBody>
                                ) : snapshot ? (
                                    <>
                                        <CardHeader className="bg-white border-0 pt-3 px-3 pb-2 d-flex align-items-center gap-2 flex-wrap">
                                            <div className="flex-grow-1">
                                                <h6 className="fw-semibold mb-1">
                                                    {snapshot.employee.name || snapshot.employee.empId}
                                                </h6>
                                                <span className="text-muted small">
                                                    {snapshot.employee.empId}
                                                    {snapshot.employee.role       ? ` · ${snapshot.employee.role}`       : ""}
                                                    {snapshot.employee.department ? ` · ${snapshot.employee.department}` : ""}
                                                </span>
                                            </div>
                                            <Button
                                                color="primary"
                                                size="sm"
                                                onClick={openGrant}
                                                disabled={working || availableToGrant.length === 0}
                                            >
                                                <i className="ri-add-line align-bottom me-1"></i>
                                                Grant permission
                                            </Button>
                                        </CardHeader>
                                        <CardBody className="p-3">
                                            {snapshot.permissions.length === 0 ? (
                                                <div className="text-center text-muted py-4 small">
                                                    No permissions granted to this user yet.
                                                </div>
                                            ) : (
                                                <Table responsive borderless className="mb-0 align-middle">
                                                    <thead>
                                                        <tr style={{ fontSize: 12, color: "var(--ssw-fg-2, #8a93a8)", textTransform: "uppercase", letterSpacing: 0.4 }}>
                                                            <th style={{ width: "40%" }}>Permission</th>
                                                            <th style={{ width: "40%" }}>Description</th>
                                                            <th style={{ width: "12%" }}>Source</th>
                                                            <th style={{ width: "8%" }} className="text-end">&nbsp;</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {snapshot.permissions.map((p) => (
                                                            <tr key={p.permissionName} style={{ borderTop: "1px solid var(--ssw-line, #eef0f4)" }}>
                                                                <td className="fw-medium">{p.permissionName}</td>
                                                                <td className="text-muted small">{p.description || "—"}</td>
                                                                <td>
                                                                    <Badge color={SOURCE_COLORS[p.source] || "secondary"} pill style={{ fontWeight: 500 }}>
                                                                        {p.source}
                                                                    </Badge>
                                                                </td>
                                                                <td className="text-end">
                                                                    {p.source === "Direct" ? (
                                                                        <Button
                                                                            color="link"
                                                                            size="sm"
                                                                            className="text-danger p-1"
                                                                            disabled={working}
                                                                            onClick={() => onRevoke(p.permissionName)}
                                                                            title="Revoke this direct grant"
                                                                        >
                                                                            <i className="ri-delete-bin-line"></i>
                                                                        </Button>
                                                                    ) : (
                                                                        <span
                                                                            className="text-muted"
                                                                            title={p.source === "Role"
                                                                                ? "Comes from the user's role — change the role to remove."
                                                                                : "Comes from the user's department — change the department to remove."}
                                                                        >
                                                                            <i className="ri-lock-2-line"></i>
                                                                        </span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            )}
                                        </CardBody>
                                    </>
                                ) : null}
                            </Card>
                        </Col>
                    </Row>
                )}

                {/* Grant-permission modal */}
                <Modal isOpen={grantOpen} toggle={() => setGrantOpen(false)}>
                    <ModalHeader toggle={() => setGrantOpen(false)}>Grant permission</ModalHeader>
                    <ModalBody>
                        <p className="text-muted small mb-3">
                            Pick a permission to grant directly to{" "}
                            <strong>{snapshot?.employee?.name || selectedEmpId}</strong>.
                            This is in addition to whatever their role already provides.
                        </p>
                        <Input
                            type="select"
                            value={pendingPerm}
                            onChange={(e) => setPendingPerm(e.target.value)}
                        >
                            <option value="">— Select a permission —</option>
                            {availableToGrant.map((p) => (
                                <option key={p.permissionName} value={p.permissionName}>
                                    {p.permissionName}
                                    {p.description ? ` — ${p.description}` : ""}
                                </option>
                            ))}
                        </Input>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="light" onClick={() => setGrantOpen(false)} disabled={working}>Cancel</Button>
                        <Button color="primary" onClick={onGrant} disabled={working || !pendingPerm}>
                            {working ? <><Spinner size="sm" /> Saving…</> : "Grant"}
                        </Button>
                    </ModalFooter>
                </Modal>
            </Container>
        </div>
    );
}
